import {CarouselClickHandleClick} from 'helpers-ga4';
customElements.component = 'swiperjs-slider-auto';

if (!customElements.get(customElements.component)) {
  class SwiperSliderAuto extends HTMLElement {
    constructor() {
      super();
      this.initSlider();
    }

    initSlider() {
      const self = this;
      const section = this.closest('section');

      const noRoleDescription = this.dataset.noAriaRoledescription === 'true';
      if (!noRoleDescription && section)
        section.setAttribute('aria-roledescription', 'carousel');

      const options = this.getOptions();
      this.slider = new Swiper(this, options);

      if (options?.a11y?.wrapperLiveRegion === false) {
        const wrapper =
          this.slider?.wrapperEl || this.querySelector('.swiper-wrapper');
        if (wrapper) {
          wrapper.removeAttribute('aria-live');
        }
      }
    }

    getOptions() {
      const self = this;
      const data = this.dataset;

      const toNumber = (value, fallback) => {
        if (value === undefined) return fallback;
        const num = Number(value);
        return isNaN(num) ? fallback : num;
      };

      let extraOptions = {};
      if (data.options) {
        try {
          extraOptions = JSON.parse(data.options);
        } catch (e) {
          console.warn('swiperjs-slider: invalid JSON in data-options');
        }
      }

      const scope = this.dataset.externalNav === 'true' ? document : this;

      const nextEl = data.nextButtonClass
        ? scope.querySelector(data.nextButtonClass)
        : this.querySelector('.swiper-button-next');

      const prevEl = data.prevButtonClass
        ? scope.querySelector(data.prevButtonClass)
        : this.querySelector('.swiper-button-prev');

      const enableFocusWrapOnArrow = data.focusWrapOnArrow === 'true';

      const enableCustomNextPrevNotification =
        data.customNextPrevNotification === 'true';

      const bulletLabelMode = data.bulletLabelMode || 'slide';

      const bulletLabelDesktopBreakpoint = toNumber(
        data.bulletLabelDesktopBreakpoint,
        1025,
      );

      const paginationEl = data.paginateClass
        ? this.querySelector(data.paginateClass)
        : this.querySelector('.swiper-pagination');

      const scrollbarEl = this.querySelector('.swiper-scrollbar');

      const rendera11yBullet =
        data.a11yBullets === 'true'
          ? function (index, className) {
              const totalSlides = this.slides.length;
              const ariaCurrent = index === 0 ? ' aria-selected="true"' : '';
              const tabIndex = index === 0 ? '0' : '-1';
              return `<button id="${this.el.dataset.sectionId}-dot-${index + 1}" aria-controls="Slide-${this.el.dataset.sectionId}-${index + 1}" type="button"${ariaCurrent} role="tab" aria-label="Slide ${index + 1} of ${totalSlides}" tabindex="${tabIndex}" class="${className} bullet-${index}"></button>`;
            }
          : undefined;

      const getRealSlideCount = (swiper) => {
        if (!swiper?.slides?.length) return 0;

        if (!swiper.params.loop) {
          return swiper.slides.length;
        }

        const slideIndexes = new Set(
          Array.from(swiper.slides)
            .map((slide) => slide.getAttribute('data-swiper-slide-index'))
            .filter((slideIndex) => slideIndex !== null),
        );

        return slideIndexes.size || swiper.slides.length;
      };

      const getVisibleRangeForBullet = (swiper, bulletIndex) => {
        const snapPosition = swiper.snapGrid?.[bulletIndex];
        const totalSlides = getRealSlideCount(swiper);

        if (
          snapPosition === undefined ||
          !swiper.slidesGrid?.length ||
          !swiper.slidesSizesGrid?.length ||
          !swiper.size
        ) {
          return {
            start: Math.min(bulletIndex, Math.max(totalSlides - 1, 0)),
            end: Math.min(bulletIndex, Math.max(totalSlides - 1, 0)),
          };
        }

        const viewportStart = snapPosition;
        const viewportEnd = snapPosition + swiper.size;
        const epsilon = 1;

        let firstFullyVisible = -1;
        let lastFullyVisible = -1;
        let firstVisible = -1;
        let lastVisible = -1;

        swiper.slidesGrid.forEach((slideStart, slideIndex) => {
          const slideSize = swiper.slidesSizesGrid[slideIndex] || 0;
          const slideEnd = slideStart + slideSize;

          const isVisible =
            slideEnd > viewportStart + epsilon &&
            slideStart < viewportEnd - epsilon;

          const isFullyVisible =
            slideStart >= viewportStart - epsilon &&
            slideEnd <= viewportEnd + epsilon;

          if (isVisible) {
            if (firstVisible === -1) {
              firstVisible = slideIndex;
            }

            lastVisible = slideIndex;
          }

          if (isFullyVisible) {
            if (firstFullyVisible === -1) {
              firstFullyVisible = slideIndex;
            }

            lastFullyVisible = slideIndex;
          }
        });

        const toRealIndex = (slideIndex) => {
          if (!swiper.params.loop) return slideIndex;
          const slide = swiper.slides[slideIndex];
          if (!slide) return slideIndex;
          const attr = slide.getAttribute('data-swiper-slide-index');
          return attr !== null ? parseInt(attr, 10) : slideIndex;
        };

        if (firstFullyVisible !== -1 && lastFullyVisible !== -1) {
          return {
            start: toRealIndex(firstFullyVisible),
            end: toRealIndex(lastFullyVisible),
          };
        }

        if (firstVisible !== -1 && lastVisible !== -1) {
          return {
            start: toRealIndex(firstVisible),
            end: toRealIndex(lastVisible),
          };
        }

        return {
          start: Math.min(bulletIndex, Math.max(totalSlides - 1, 0)),
          end: Math.min(bulletIndex, Math.max(totalSlides - 1, 0)),
        };
      };

      // hides the shared prev/next wrapper (not just the buttons) once all slides fit without scrolling
      const updateArrowsLockState = (swiper) => {
        const {nextEl, prevEl} = swiper.navigation || {};
        const wrapper =
          (nextEl && prevEl && nextEl.parentElement === prevEl.parentElement
            ? nextEl.parentElement
            : null) ||
          nextEl?.parentElement ||
          prevEl?.parentElement;
        wrapper?.classList.toggle('swiper-arrows-lock', !!swiper.isLocked);
      };

      const syncBulletLabels = (swiper) => {
        if (data.a11yBullets !== 'true') return;

        const bullets = swiper.pagination?.bullets || [];
        if (!bullets.length) return;

        const useVisibleRangeLabels =
          bulletLabelMode === 'visible-range-desktop' &&
          window.innerWidth >= bulletLabelDesktopBreakpoint;

        const totalSlides = getRealSlideCount(swiper);
        const paginationLists = swiper.el.querySelectorAll('[role="tablist"]');

        paginationLists.forEach((paginationList) => {
          paginationList.setAttribute(
            'aria-label',
            useVisibleRangeLabels
              ? 'Choose items to display'
              : 'Choose slide to display',
          );
        });

        bullets.forEach((bullet, bulletIndex) => {
          if (!useVisibleRangeLabels) {
            bullet.setAttribute(
              'aria-label',
              `Slide ${bulletIndex + 1} of ${totalSlides}`,
            );
            return;
          }

          const {start, end} = getVisibleRangeForBullet(swiper, bulletIndex);
          const startItem = Math.min(start + 1, totalSlides);
          const endItem = Math.min(end + 1, totalSlides);

          bullet.setAttribute(
            'aria-label',
            startItem === endItem
              ? `Show item ${startItem} of ${totalSlides}`
              : `Show items ${startItem} to ${endItem} of ${totalSlides}`,
          );
        });
      };

      const handleBulletKeydown = (e, tabs, currentIndex, moveFocus = true) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(e.key)) return;
        e.preventDefault();

        if (currentIndex === -1) return;

        let nextIndex = currentIndex;
        if (e.key === 'ArrowRight')
          nextIndex = (currentIndex + 1) % tabs.length;
        if (e.key === 'ArrowLeft')
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (e.key === 'Home') nextIndex = 0;
        if (e.key === 'End') nextIndex = tabs.length - 1;

        const target = tabs[nextIndex];
        if (target) {
          if (moveFocus) {
            target.click();
            target.focus();
          } else {
            const previousFocus = document.activeElement;
            target.click();
            if (previousFocus) previousFocus.focus();
          }
        }
      };

      const defaultOptions = {
        slidesPerView: 'auto',
        spaceBetween: 12,
        loop: true,
        speed: 400,
        centeredSlides: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        pagination: paginationEl
          ? {
              el: paginationEl,
              dynamicBullets: true,
              ...(rendera11yBullet && {renderBullet: rendera11yBullet}),
            }
          : false,
        navigation:
          nextEl || prevEl
            ? {
                nextEl: nextEl || undefined,
                prevEl: prevEl || undefined,
              }
            : false,
        scrollbar: scrollbarEl
          ? {
              el: scrollbarEl,
            }
          : false,
        breakpoints: {
          0: {
            centeredSlides: true,
            spaceBetween: 12,
          },
          1025: {
            spaceBetween: 20,
          },
          1600: {
            centeredSlides: false,
          },
        },
        on: {
          imagesReady: () => {
            this.classList.add('swiper-loaded');
          },
          init: function () {
            if (this.el?.dataset?.focusOnArrow === 'true') {
              setTimeout(() => {
                updateSlideFocusability.call(this, false);
              }, 50);
            }

            updateArrowsLockState(this);
            syncBulletLabels(this);
          },
          afterInit: function () {
            if (this.el?.dataset?.loadPlaceholders === 'true') {
              this.el
                .querySelectorAll('placeholder-box')
                .forEach((el) => el.classList.add('loaded'));
            }

            if (this.el?.dataset?.a11yBullets === 'true') {
              const tablist = this.el?.querySelector(
                '.swiper-pagination-bullets',
              );
              if (tablist) {
                tablist.addEventListener('keydown', (e) => {
                  const tabs = [...tablist.querySelectorAll('[role="tab"]')];
                  const currentIndex = tabs.indexOf(document.activeElement);
                  handleBulletKeydown(e, tabs, currentIndex);
                });

                if (this.el.dataset.arrowClicksDots === 'true') {
                  this.el.addEventListener('keydown', (e) => {
                    if (tablist.contains(e.target)) return;
                    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
                    const activeBullet = tablist.querySelector(
                      '.swiper-pagination-bullet-active',
                    );
                    const currentIndex = activeBullet
                      ? tabs.indexOf(activeBullet)
                      : -1;
                    handleBulletKeydown(e, tabs, currentIndex, false);
                  });
                }
              }
            }

            syncBulletLabels(this);

            if (data.autoPlay) autoPlay(this);
          },
          slideChange: function (swiper) {
            const eventName = this.el?.dataset?.slideChangeEvent;
            if (eventName && !swiper.isEnd) {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const firstFullyVisibleSlide = Array.from(swiper.slides).find(
                (slide) =>
                  slide.classList.contains('swiper-slide-fully-visible'),
              );
              this.el.dispatchEvent(
                new CustomEvent(eventName, {
                  bubbles: true,
                  detail: {swiper: this, activeSlide, firstFullyVisibleSlide},
                }),
              );
            }
            if (this.el?.dataset?.focusOnArrow === 'true') {
              setTimeout(() => {
                updateSlideFocusability.call(this, true);
              }, 50);
            }

            if (this.el?.dataset?.a11yBullets === 'true') {
              const bullets = this.el.querySelectorAll(
                '.swiper-pagination-bullet',
              );
              bullets.forEach((bullet) => {
                bullet.setAttribute('aria-selected', 'false');
                bullet.setAttribute('tabindex', '-1');
              });
              const active = this.el.querySelector(
                '.swiper-pagination-bullet-active',
              );
              if (active) {
                active.setAttribute('aria-selected', 'true');
                active.removeAttribute('tabindex');
              }
            }

            syncBulletLabels(this);
          },
          resize: function () {
            updateArrowsLockState(this);
            syncBulletLabels(this);
          },
          breakpoint: function () {
            updateArrowsLockState(this);
            syncBulletLabels(this);
          },
          reachEnd: function (swiper) {
            swiper.el.setAttribute('data-at-end', '');
            const eventName = swiper.el?.dataset?.slideChangeEvent;
            if (eventName) {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const firstFullyVisibleSlide = Array.from(swiper.slides).find(
                (slide) =>
                  slide.classList.contains('swiper-slide-fully-visible'),
              );
              swiper.el.dispatchEvent(
                new CustomEvent(eventName, {
                  bubbles: true,
                  detail: {swiper, activeSlide, firstFullyVisibleSlide},
                }),
              );
            }
          },
          fromEdge: function (swiper) {
            swiper.el.removeAttribute('data-at-end');
          },
          navigationNext: function (swiper) {
            ga4ArrowsNotification(swiper, 'next');
            if (enableCustomNextPrevNotification) {
              const notificationEl = swiper.el.querySelector(
                '.petivity-swiper-custom-notification',
              );
              if (notificationEl) {
                const slides = Array.from(swiper?.slides ?? []);
                const visibleIndexes = Array.from(
                  swiper.el.querySelectorAll(
                    '.swiper-slide-active, .swiper-slide-fully-visible',
                  ),
                )
                  .map((slide) => slide.dataset.itemIndex)
                  .filter((i) => i > 0);

                if (visibleIndexes.length > 0 && slides.length > 0) {
                  const formattedIndexes =
                    visibleIndexes.length === 1
                      ? String(visibleIndexes[0])
                      : visibleIndexes.slice(0, -1).join(', ') +
                        ' and ' +
                        visibleIndexes[visibleIndexes.length - 1];
                  const displayText = `Displaying ${visibleIndexes.length === 1 ? 'slide' : 'slides'} ${formattedIndexes} of ${slides.length}`;

                  if (notificationEl.textContent !== displayText) {
                    notificationEl.textContent = displayText;
                  }
                  setTimeout(() => {
                    notificationEl.textContent = '';
                  }, 2000);
                }
              }
            }

            if (enableFocusWrapOnArrow) {
              if (!swiper.isEnd) return;
              const prevSelector =
                swiper.el?.dataset?.prevButtonClass || '.swiper-button-prev';
              const scope =
                swiper.el?.dataset.externalNav === 'true'
                  ? document
                  : swiper.el;
              const prevButton = scope.querySelector(prevSelector);

              if (prevButton) {
                prevButton.focus();
              }
            }
          },
          navigationPrev: function (swiper) {
            ga4ArrowsNotification(swiper, 'previous');
            if (enableCustomNextPrevNotification) {
              const notificationEl = swiper.el.querySelector(
                '.petivity-swiper-custom-notification',
              );
              if (notificationEl) {
                const slides = Array.from(swiper?.slides ?? []);
                const visibleIndexes = Array.from(
                  swiper.el.querySelectorAll(
                    '.swiper-slide-active, .swiper-slide-fully-visible',
                  ),
                )
                  .map((slide) => slide.dataset.itemIndex)
                  .filter((i) => i > 0);
                if (visibleIndexes.length > 0 && slides.length > 0) {
                  const formattedIndexes =
                    visibleIndexes.length === 1
                      ? String(visibleIndexes[0])
                      : visibleIndexes.slice(0, -1).join(', ') +
                        ' and ' +
                        visibleIndexes[visibleIndexes.length - 1];
                  const displayText = `Displaying ${visibleIndexes.length === 1 ? 'slide' : 'slides'} ${formattedIndexes} of ${slides.length}`;
                  if (notificationEl.textContent !== displayText) {
                    notificationEl.textContent = displayText;
                  }
                  setTimeout(() => {
                    notificationEl.innerText = '';
                  }, 2000);
                }
              }
            }

            if (enableFocusWrapOnArrow) {
              if (!swiper.isBeginning) return;
              const nextSelector =
                swiper.el?.dataset?.nextButtonClass || '.swiper-button-next';
              const scope =
                swiper.el?.dataset.externalNav === 'true'
                  ? document
                  : swiper.el;
              const nextButton = scope.querySelector(nextSelector);
              if (nextButton) {
                nextButton.focus();
              }
            }
          },
          touchEnd(swiper, event) {
            if (swiper.touches.diff !== 0) {
              ga4ArrowsNotification(swiper, 'swipe');
            }
          },
        },
      };

      function updateSlideFocusability(shouldMoveFocus) {
        const visibleSlides = [];
        const invisibleSlides = [];

        this.slides.forEach((slide, index) => {
          const isLastSlideAtEnd =
            this.isEnd && index >= this.slides.length - 1;

          const isVisible =
            slide.classList.contains('swiper-slide-fully-visible') ||
            slide.classList.contains('swiper-slide-active') ||
            isLastSlideAtEnd;

          if (isVisible) {
            visibleSlides.push(slide);
          } else {
            invisibleSlides.push(slide);
          }
        });

        if (shouldMoveFocus && document.activeElement) {
          const focusedSlide = Array.from(this.slides).find((slide) =>
            slide.contains(document.activeElement),
          );
          if (focusedSlide) {
            const activeSlide = this.slides[this.activeIndex];
            const firstFocusable = activeSlide?.querySelector(
              'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );

            if (firstFocusable && activeSlide) {
              firstFocusable.focus();
            }
          }
        }

        visibleSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll(
            'a, button, input, select, textarea, [tabindex]',
          );
          focusableElements.forEach((el) => {
            if (el.hasAttribute('data-keep-hidden')) {
              return;
            }
            if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
            el.removeAttribute('aria-hidden');
          });
          //if not visible aria-hidden should be on the whole card
          if (slide.classList.contains('swiper-slide-visible')) {
            slide.removeAttribute('aria-hidden');
          } else {
            slide.setAttribute('aria-hidden', 'true');
          }
        });

        invisibleSlides.forEach((slide) => {
          const focusableElements = slide.querySelectorAll(
            'a, button, input, select, textarea, [tabindex]',
          );
          focusableElements.forEach((el) => {
            el.setAttribute('tabindex', '-1');
            el.setAttribute('aria-hidden', 'true');
          });
          slide.setAttribute('aria-hidden', 'true');
        });
      }

      function ga4ArrowsNotification(swiper, direction) {
        const activeSlide = swiper.el.querySelector('.swiper-slide-active');
        const slideAsset = activeSlide.querySelector(
          swiper.el.dataset.ga4ClassImage,
        );
        const item = {
          page_type: `${document.title}`,
          carousel_location: `${window.location.pathname}`,
          carousel_name: swiper.el.dataset.ga4Title,
          interaction_type: 'arrow_click',
          carousel_direction: direction,
          carousel_item_index: activeSlide.dataset.itemIndex,
          carousel_item_label: slideAsset?.getAttribute('alt') || '',
          media_type: swiper.el.dataset.ga4MediaType,
          carousel_type: swiper.el.dataset.ga4CarouselType,
        };
        CarouselClickHandleClick(item);
      }

      function autoPlay(swiper) {
        const swiperEl = self;
        const wrapperTrack = swiper.wrapperEl;
        let isHardwarePaused = false;
        const section = swiperEl.closest('section');

        // --- HARDWARE BUTTON LOGIC ---
        const toggleBtn = section.querySelector('.toggle-btn');

        const playIcon = toggleBtn.querySelector('.play-icon');
        const pauseIcon = toggleBtn.querySelector('.pause-icon');

        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
        toggleBtn.setAttribute('aria-pressed', 'false');

        // --- STEP 1: DYNAMIC SLIDE CLONING SAFETY BUFFER ---
        const originalSlides = Array.from(wrapperTrack.children);

        // An edge-to-edge marquee usually needs at least 15-20 nodes
        // to complete loops smoothly across 4K displays.
        const minimumRequiredSlides = 20;

        if (
          originalSlides.length > 0 &&
          originalSlides.length < minimumRequiredSlides
        ) {
          const cloneLoops = Math.ceil(
            minimumRequiredSlides / originalSlides.length,
          );

          // Clone nodes cleanly until the slide budget criteria is safely cleared
          for (let i = 0; i < cloneLoops; i++) {
            originalSlides.forEach((slide) => {
              const clone = slide.cloneNode(true);
              wrapperTrack.appendChild(clone);
            });
          }
        }

        // --- HOVER HANDLING: VIA PURE CSS CORRELATION ---
        swiperEl.addEventListener('mouseenter', () => {
          if (isHardwarePaused) return;

          // Read the exact matrix position of the moving track
          const computedStyle = window.getComputedStyle(wrapperTrack);
          const matrix = new WebKitCSSMatrix(computedStyle.transform);
          const currentX = matrix.m41; // Grabs the precise horizontal pixel transform coordinates

          // Save the location inside a CSS Variable and inject the freeze class instantly
          wrapperTrack.style.setProperty('--freeze-x', `${currentX}px`);
          wrapperTrack.classList.add('paused');

          swiper.autoplay.stop();

          playIcon.style.display = 'block';
          pauseIcon.style.display = 'none';
          toggleBtn.setAttribute('aria-pressed', 'true');
        });

        swiperEl.addEventListener('mouseleave', () => {
          if (isHardwarePaused) return;

          // Safely strip the override class so the native timeline can seamlessly take control
          wrapperTrack.classList.remove('paused');
          swiper.autoplay.start();

          playIcon.style.display = 'none';
          pauseIcon.style.display = 'block';
          toggleBtn.setAttribute('aria-pressed', 'false');
        });

        toggleBtn.addEventListener('click', () => {
          if (!isHardwarePaused) {
            // Enforce absolute hardware freeze
            const computedStyle = window.getComputedStyle(wrapperTrack);
            const matrix = new WebKitCSSMatrix(computedStyle.transform);
            const currentX = matrix.m41;

            wrapperTrack.style.setProperty('--freeze-x', `${currentX}px`);
            wrapperTrack.classList.add('paused');
            swiper.autoplay.stop();

            playIcon.style.display = 'block';
            pauseIcon.style.display = 'none';
            toggleBtn.setAttribute('aria-pressed', 'true');
            isHardwarePaused = true;
          } else {
            isHardwarePaused = false;

            // Only trigger movement on play if user isn't holding their mouse over the area
            if (!swiperEl.matches(':hover')) {
              wrapperTrack.classList.remove('paused');
              swiper.autoplay.start();
            }

            playIcon.style.display = 'none';
            pauseIcon.style.display = 'block';
            toggleBtn.setAttribute('aria-pressed', 'false');
          }
        });
      }

      const merged = {
        ...defaultOptions,
        ...extraOptions,
      };

      if (
        defaultOptions.pagination &&
        extraOptions.pagination &&
        typeof extraOptions.pagination === 'object'
      ) {
        merged.pagination = {
          ...defaultOptions.pagination,
          ...extraOptions.pagination,
        };
      }

      return merged;
    }
  }

  customElements.define(customElements.component, SwiperSliderAuto);
}
